import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';

/**
 * Monthly / annual billing interval selector for pricing and billing pages.
 * Active annual uses filled accent; active monthly uses outline accent.
 */
export default function BillingIntervalToggle({ value, onChange, style, accentColor }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const activeFill = accentColor || colors.primary;

  const getButtonStyle = (id, active) => {
    const isMonthly = id === 'monthly';
    const base = {
      borderRadius: 6,
      border: '1px solid transparent',
    };

    if (active && isMonthly) {
      return {
        ...base,
        background: `color-mix(in srgb, ${activeFill} 14%, transparent)`,
        color: activeFill,
        fontWeight: 600,
        borderColor: activeFill,
      };
    }

    if (active) {
      return {
        ...base,
        background: activeFill,
        color: '#fff',
        fontWeight: 600,
        borderColor: activeFill,
      };
    }

    return {
      ...base,
      background: 'transparent',
      color: colors.textMuted,
      fontWeight: 500,
    };
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 3,
        borderRadius: 9,
        border: `1px solid ${colors.border}`,
        background: 'rgba(255, 255, 255, 0.02)',
        ...style,
      }}
      role="group"
      aria-label="Billing interval"
    >
      {[
        { id: 'monthly', label: 'Monthly' },
        { id: 'annual', label: 'Annual', badge: '2 months free' },
      ].map(({ id, label, badge }) => {
        const active = value === id;
        const buttonStyle = getButtonStyle(id, active);

        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: isMobile ? '6px 12px' : '8px 16px',
              cursor: 'pointer',
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              ...buttonStyle,
            }}
          >
            {label}
            {badge && (
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: 5,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  background: active ? 'rgba(255,255,255,0.22)' : activeFill,
                  color: '#fff',
                }}
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
